import { Plan, UNLIMITED } from '../types';

/** Whole euros — every published price is a round number. */
export function euros(cents: number): string {
  return `€${Math.round(cents / 100)}`;
}

export function formatLimit(value: number): string {
  return value === UNLIMITED ? 'Unlimited' : String(value);
}

export function formatInterval(seconds: number): string {
  return seconds >= 60 ? `${seconds / 60}-minute` : `${seconds}-second`;
}

export function plural(count: number, singular: string, plural?: string): string {
  const word = count === 1 ? singular : (plural ?? `${singular}s`);
  return `${formatLimit(count)} ${word}`;
}

/** Percentage saved by paying annually, or 0 when the tier has no discount. */
export function annualSaving(plan: Plan): number {
  if (plan.price_monthly_cents <= 0 || plan.price_annual_monthly_cents >= plan.price_monthly_cents) {
    return 0;
  }
  const saved = plan.price_monthly_cents - plan.price_annual_monthly_cents;
  return Math.round((saved / plan.price_monthly_cents) * 100);
}

/**
 * The concrete things a tier grants, derived from the plan itself rather than
 * written out per tier — so a limit can never be changed in the catalogue and
 * left stale in the marketing copy.
 */
export function planFeatures(plan: Plan): string[] {
  const features = [
    plural(plan.max_monitors, 'monitor'),
    `${formatInterval(plan.min_interval_seconds)} check interval`,
    plural(plan.max_seats, 'seat'),
    plural(plan.max_status_pages, 'status page'),
    `${plan.retention_months}-month data retention`,
    plan.monitor_types && plan.monitor_types.length > 0
      ? 'HTTP, ping, port, keyword & heartbeat monitors'
      : 'All monitor types, incl. SSL, DNS, domain & API',
  ];

  if (plan.custom_domain) features.push('Status pages on your own domain');
  if (plan.white_label) features.push('White-label status pages');

  return features;
}

/** Rows for the side-by-side comparison, all derived from the catalogue. */
export interface ComparisonRow {
  label: string;
  value: (plan: Plan) => string;
}

export const comparisonRows: ComparisonRow[] = [
  { label: 'Monitors', value: p => formatLimit(p.max_monitors) },
  { label: 'Check interval', value: p => formatInterval(p.min_interval_seconds) },
  { label: 'Team seats', value: p => formatLimit(p.max_seats) },
  { label: 'Status pages', value: p => formatLimit(p.max_status_pages) },
  { label: 'Alert contacts', value: p => formatLimit(p.max_alert_contacts) },
  { label: 'Data retention', value: p => plural(p.retention_months, 'month') },
  {
    label: 'Advanced monitors (SSL, DNS, domain, API, UDP)',
    value: p => (p.monitor_types && p.monitor_types.length > 0 ? '—' : 'Included'),
  },
  { label: 'Custom domain for status pages', value: p => (p.custom_domain ? 'Included' : '—') },
  { label: 'White-label status pages', value: p => (p.white_label ? 'Included' : '—') },
];
